import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DataTableCard from '../../Components/Table/DataTableCard';
import Pagination from '../../Components/Table/Pagination';

const Employees = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); // { message: '', type: 'success' }

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current: 0, next: null, totalPage: 1, totalRows: 0 });

  useEffect(() => {
    if (location.state?.toastMessage) {
      setToast({
        message: location.state.toastMessage,
        type: location.state.toastType || 'success'
      });
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const getEmployees = async () => {
      try {
        setLoading(true);
        const result = await api.getEmployees({ page, pageSize: 10 });
        setEmployees(result.result);

        setPagination({
          current: result.current,
          next: result.next,
          totalPage: result.totalPage || 1,
          totalRows: result.totalRows || 0
        });
      } catch (error) {
        console.error('Error fetching employee', error);
      } finally {
        setLoading(false);
      }
    };
    getEmployees();
  }, [page]);

  return (
    <div className="container-fluid p-0">
      <DataTableCard
        icon="bi-people-fill"
        title="Daftar Karyawan"
        subtitle="Kelola data karyawan dan periode kerja."
        actions={
          <Link to="/employee/add" className="btn btn-primary rounded-pill px-3">
            <i className="bi bi-plus-circle me-2"></i>Tambah
          </Link>
        }
      >
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 py-3 border-0">Karyawan</th>
                <th className="border-0 py-3">LinkedIn</th>
                <th className="border-0 py-3">Periode Kerja</th>
                <th className="border-0 py-3">Status</th>
                <th className="border-0 py-3 text-end pe-4">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="placeholder-glow">
                    <td className="ps-4 py-3"><span className="placeholder col-8 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-6 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-10 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-4 rounded"></span></td>
                    <td className="text-end pe-4 py-3"><span className="placeholder col-4 rounded"></span></td>
                  </tr>
                ))
              ) : employees?.length > 0 ? (
                employees.map((emp) => (
                  <tr
                    key={emp.id}
                    className="cursor-pointer transition-all"
                    onClick={() => navigate(`/employee/${emp.id}`)}
                  >
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-person-circle text-primary fs-3 me-3"></i>
                        <div>
                          <div className="fw-bold text-dark">
                            {emp.firstName} {emp.lastName}
                          </div>
                          <small className="text-muted">
                            {emp.email || 'No email'}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      {emp.linkedIn ? (
                        <a
                          href={`https://${emp.linkedIn}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-linkedin text-primary me-2"></i>
                          {emp.linkedIn}
                        </a>
                      ) : (
                        <span className="text-muted extra-small">-</span>
                      )}
                    </td>
                    <td className="py-3">
                      <span className="extra-small fw-medium text-muted bg-light px-2 py-1 rounded">
                        <i className="bi bi-calendar-event me-1"></i>
                        {emp.dateStartWork
                          ? `${emp.dateStartWork} - ${emp.dateEndWork || 'Present'}`
                          : 'No work period'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span
                        className={`badge bg-${emp.isActive ? 'success' : 'secondary'} bg-opacity-10 text-${emp.isActive ? 'success' : 'secondary'} rounded-pill px-3 py-2`}
                      >
                        {emp.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 text-end pe-4">
                      <button
                        className="btn btn-outline-primary btn-sm rounded-circle"
                        style={{ width: '32px', height: '32px' }}
                        title="Detail"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/employee/${emp.id}`);
                        }}
                      >
                        <i className="bi bi-eye-fill"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="text-muted mb-3 opacity-25">
                      <i className="bi bi-person-exclamation fs-1"></i>
                    </div>
                    <h6 className="fw-bold text-dark mb-1">Belum ada karyawan</h6>
                    <p className="text-muted small mb-0">
                      Tidak ada karyawan yang terdaftar untuk saat ini.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPage}
          onPageChange={setPage}
        />
      </DataTableCard>

      {/* Bootstrap Toast Notification Container */}
      {toast && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div
            className={`toast show align-items-center text-white bg-${toast.type === 'danger' ? 'danger' : 'success'} border-0 shadow-lg`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">
                <i className={`bi ${toast.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2`}></i>
                {toast.message}
              </div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setToast(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
